import React from 'react';
import { toast } from 'react-toastify';
import { flatMap } from 'lodash';
import { HINDSIGHTING } from 'src/utils/Domain/Constants';

import type { RestoreSessionState, RestoreSessionProps } from './RestoreSession.container';
import { getSessionAll } from './RestoreSession.utils';
import type { Perspective, Bubble, BoundPerspective } from 'configuration-schemas/bindings.types';
import ServiceContainer from 'src/ServiceContainer';
import { getPerspectiveLookupKey, getPerspectiveConfig } from 'src/services/configuration/service.utils';
import { type PerspectiveConfig, isPerspective, isEnabledPerspective } from 'src/utils/Domain/Perspective';
import './RestoreSession.scss';

class RestoreSession extends React.Component<RestoreSessionProps, RestoreSessionState> {
  state = {
    loading: false,
  };

  applyNav = async (perspective: Perspective, activeTab: string, perspectiveConfig: PerspectiveConfig) => {
    const groups = perspectiveConfig.view.filter(isPerspective);
    let perspectiveBinding: BoundPerspective | undefined = groups.find((x) => {
      const perspectiveLookupKey = getPerspectiveLookupKey(x);
      return perspectiveLookupKey == perspective;
    });

    // if not found at top level, search inside nested Bubble children
    if (!perspectiveBinding) {
      const bubbleChildren = flatMap(
        perspectiveConfig.view.filter((x): x is Bubble => x.type === 'Bubble'),
        (bubble) => bubble.view as unknown as BoundPerspective[],
      );
      perspectiveBinding = bubbleChildren.find((p) => getPerspectiveLookupKey(p) == perspective);
    }

    if (perspectiveBinding && isEnabledPerspective(perspectiveBinding)) {
      this.props.setPerspective(perspectiveBinding);
      this.props.setActiveTab(activeTab);
    }
  };

  applySession = async (perspective: Perspective, tab: string | null) => {
    const config = await getPerspectiveConfig().then((x) => x.data);
    await this.applyNav(perspective, tab || HINDSIGHTING, config);
  };

  onClick = () => {
    const { tab, perspective, page } = getSessionAll();

    if (!perspective) return;

    this.setState({
      loading: true,
    });
    toast.dismiss('restoreSession');

    this.applySession(perspective, tab)
      .then(() => {
        if (page) {
          this.props.routerNavigate(page);
        }
      })
      .catch((e) => {
        ServiceContainer.loggingService.error('Error: Unable to restore previous session', e);
        this.destroy();
        toast.error('Error: Unable to restore previous session.', {
          position: toast.POSITION.TOP_LEFT,
        });
      });
  };

  destroy() {
    toast.dismiss('restoreSession');
    this.setState({ loading: false });
  }

  componentDidMount() {
    toast(
      <div onClick={this.onClick} className="restore-session-message">
        <p>Click here to pick up where you left off! </p>
      </div>,
      {
        position: toast.POSITION.TOP_LEFT,
        closeOnClick: false,
        autoClose: false,
        draggable: false,
        type: 'info',
        toastId: 'restoreSession',
      },
    );
  }

  componentWillUnmount() {
    this.destroy();
  }

  render() {
    return <React.Fragment />;
  }
}
export default RestoreSession;
